import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AdvisorModal from "@/components/AdvisorModal";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Plus, ArrowRight, SignOut, FileText, ChartLineUp,
  Trash, Sparkle, Calculator, TrendUp, TrendDown, HandCoins,
  PaintBrush, ArrowSquareOut, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CreditBalance from "@/components/CreditBalance";
import AdvisorSessionsInline from "@/components/AdvisorSessionsInline";
import WaitlistModal from "@/components/WaitlistModal";
import UserChip from "@/components/UserChip";
import David from "@/components/David";
import MyExports from "@/components/MyExports";
import { isProduction } from "@/lib/platform";
import { extractErrorMessage } from "@/lib/apiError";

const C = {
  bgDark: "#000",
  panel: "#0A2826",
  panelHover: "#0D3330",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
};

const ClinqLogo = ({ withTagline = true }) => (
  <div className="flex items-center gap-3">
    <span className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
      style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}>
      Cl
      <span className="relative inline-block">
        ı
        <span aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
          style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span>
      </span>
      nq
    </span>
    {withTagline && (
      <div className="hidden sm:flex flex-col leading-none border-l pl-3 border-white/15">
        <span className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: C.tealSoft }}>Funder</span>
        <span className="text-[9px] uppercase tracking-[0.22em] font-semibold mt-0.5 text-white/55">Readiness</span>
      </div>
    )}
  </div>
);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Nudge-email deep link: /dashboard?advisor=pitch_deck opens the advisor form
  const advisorParam = searchParams.get("advisor");
  const advisorService = ["pitch_deck", "financial_model", "mgmt_accounts", "general"].includes(advisorParam)
    ? advisorParam : (advisorParam ? "pitch_deck" : null);
  const closeAdvisor = () => {
    searchParams.delete("advisor");
    setSearchParams(searchParams, { replace: true });
  };
  const [models, setModels] = useState([]);
  const [decks, setDecks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [valuations, setValuations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [m, d, a, v] = await Promise.all([
          api.get("/fm/models").catch(() => ({ data: { models: [] } })),
          api.get("/pitch/decks").catch(() => ({ data: [] })),
          api.get("/mgmt/accounts").catch(() => ({ data: [] })),
          api.get("/valuations").catch(() => ({ data: [] })),
        ]);
        setModels(m.data?.models || []);
        setDecks(d.data || []);
        setAccounts(a.data || []);
        setValuations(v.data || []);
      } catch {
        toast.error("Could not load your workspace");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const deleteModel = async (id, name, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Delete model "${name}"?`)) return;
    try {
      await api.delete(`/fm/models/${id}`);
      setModels((arr) => arr.filter((m) => m.fm_id !== id));
      toast.success("Model deleted");
    } catch { toast.error("Could not delete"); }
  };

  const deleteDeck = async (id, name, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Delete deck "${name}"?`)) return;
    try {
      await api.delete(`/pitch/decks/${id}`);
      setDecks((arr) => arr.filter((d) => d.deck_id !== id));
      toast.success("Deck deleted");
    } catch { toast.error("Could not delete"); }
  };

  const deleteAccount = async (id, name, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Delete management account "${name}"?`)) return;
    try {
      await api.delete(`/mgmt/accounts/${id}`);
      setAccounts((arr) => arr.filter((a) => a.account_id !== id));
      toast.success("Account deleted");
    } catch { toast.error("Could not delete"); }
  };

  const [previewing, setPreviewing] = useState(null);   // deck_id currently previewing
  // Production locks Management Accounts / Business Valuation behind a
  // "Join the shortlist" gate — Financial Model and Pitch Deck are live.
  const locked = isProduction();
  const [waitService, setWaitService] = useState(null);
  const openWait = (service) => setWaitService(service);
  const closeWait = () => setWaitService(null);
  const gotoModel = () => navigate("/fm/new");
  const gotoAccounts = () => locked ? openWait("accounts") : navigate("/mgmt/new");
  const gotoValuation = () => locked ? openWait("business_valuation") : navigate("/valuation/new");
  const [upgrading, setUpgrading] = useState(null);     // deck_id currently upgrading

  const previewV2Design = async (deck, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(
      `Preview the new V2 design for "${deck.company_name}"?\n\n` +
      `This costs 2 credits and downloads a .pptx so you can compare it side-by-side with your current deck.\n\n` +
      `Your saved deck is NOT changed — use "Apply V2" afterwards if you like it.`
    )) return;
    setPreviewing(deck.deck_id);
    try {
      const resp = await api.post(`/pitch/decks/${deck.deck_id}/preview-design`, null, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(deck.company_name || "deck").replace(/\s+/g, "_")}_v2_preview.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("V2 preview downloaded. 2 credits charged.");
      window.dispatchEvent(new Event("clinq:credits-changed"));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Could not preview";
      toast.error(typeof detail === "string" ? detail : "Could not preview");
    } finally {
      setPreviewing(null);
    }
  };

  const upgradeV2Design = async (deck, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(
      `Permanently upgrade "${deck.company_name}" to the V2 design?\n\n` +
      `Every future export will use the new infographic-driven templates. ` +
      `This is free — but it can't be undone via this UI.`
    )) return;
    setUpgrading(deck.deck_id);
    try {
      await api.post(`/pitch/decks/${deck.deck_id}/upgrade-design`);
      setDecks((arr) => arr.map((d) => d.deck_id === deck.deck_id
        ? { ...d, design_version: "v2" } : d));
      toast.success("Upgraded to V2 design.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not upgrade"));
    } finally {
      setUpgrading(null);
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="dashboard-page">
      <WaitlistModal open={!!waitService} service={waitService || "general"} onClose={closeWait} initialEmail={user?.email || ""} />
      {advisorService && <AdvisorModal service={advisorService} onClose={closeAdvisor} />}
      <header style={{ backgroundColor: "#000" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo-dashboard"><ClinqLogo /></Link>
          <div className="flex items-center gap-4">
            <CreditBalance variant="dark" />
            <UserChip user={user} />
            <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
              className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1.5 transition-colors">
              <SignOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
        {/* Welcome / intro */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-white/50">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }}></span>
            Your workspace
          </div>
          <h1 className="font-extrabold text-5xl mt-3 tracking-tight" style={{ letterSpacing: "-0.03em" }}>
            {(!loading && !models.length && !decks.length && !accounts.length && !valuations.length) ? "Welcome" : "Welcome back"}{user?.name ? <>, <span style={{ color: C.teal }}>{user.name.split(" ")[0]}</span></> : ""}.
          </h1>
                  <div className="text-white/60 mt-3 max-w-2xl">
            Four documents stand between you and funder-ready. Pick the one to work on, or jump back into something you started.
          </div>
        </div>

        {/* Advisor sessions panel — auto-hides until the user has at least one session or outstanding invoice */}
        <div className="mb-10 max-w-2xl">
          <AdvisorSessionsInline />
        </div>

        {/* Build sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16" data-testid="build-sections">
          <BuildCard
            num="01"
            name="Pitch Deck"
            tag="Tell the story"
            status="live"
            ctaLabel={decks.length ? "+ New deck" : "Start building"}
            desc="11-slide methodology · AI-drafted · funder-ready check"
            icon={
              <svg viewBox="0 0 60 50" className="w-full h-auto">
                <rect x="4" y="13" width="32" height="26" rx="3" fill={C.panelHover} stroke={C.teal} strokeWidth="1" opacity="0.6" />
                <rect x="11" y="9" width="32" height="26" rx="3" fill={C.panel} stroke={C.teal} strokeWidth="1" opacity="0.85" />
                <rect x="18" y="5" width="38" height="32" rx="3" fill="#0D3330" stroke={C.teal} strokeWidth="1.5" />
                <rect x="22" y="10" width="18" height="3" rx="1" fill={C.teal} />
                <rect x="22" y="16" width="26" height="2" rx="1" fill="#fff" opacity="0.4" />
                <rect x="22" y="21" width="22" height="2" rx="1" fill="#fff" opacity="0.4" />
                <rect x="22" y="26" width="26" height="2" rx="1" fill="#fff" opacity="0.4" />
                <circle cx="48" cy="33" r="3" fill={C.orange} />
              </svg>
            }
            onClick={() => navigate("/pitch/new")}
            data-testid="build-card-pitch"
          />
          <BuildCard
            num="02"
            name="Financial Model"
            tag="Prove the maths"
            status="live"
            ctaLabel={models.length ? "+ New model" : "Start building"}
            desc="3+5 year forecast · live AI co-pilot · Excel export"
            icon={
              <svg viewBox="0 0 60 50" className="w-full h-auto">
                <rect x="4" y="4" width="52" height="42" rx="3" fill={C.panel} stroke={C.teal} strokeWidth="1.5" />
                <rect x="4" y="4" width="52" height="7" rx="3" fill={C.teal} opacity="0.25" />
                <line x1="17" y1="4" x2="17" y2="46" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
                <line x1="30" y1="4" x2="30" y2="46" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
                <line x1="43" y1="4" x2="43" y2="46" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
                <line x1="4" y1="20" x2="56" y2="20" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
                <line x1="4" y1="32" x2="56" y2="32" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
                <path d="M9 40 L21 30 L33 22 L45 16 L53 9" stroke={C.orange} strokeWidth="1.8" fill="none" strokeLinecap="round" />
                <circle cx="53" cy="9" r="2" fill={C.orange} />
              </svg>
            }
            onClick={gotoModel}
            data-testid="build-card-model"
          />
          <BuildCard
            num="03"
            name="Management Accounts"
            tag={locked ? "Coming soon" : "Reconcile monthly"}
            status={locked ? "waitlist" : "live"}
            ctaLabel={locked ? "Join the shortlist" : (accounts.length ? "+ New month" : "Start building")}
            desc={locked
              ? "Excel · bank · M-Pesa → P&L, balance sheet, cashflow — notify me when it goes live."
              : "Excel · bank · M-Pesa → P&L, balance sheet, cashflow"}
            icon={
              <svg viewBox="0 0 60 50" className="w-full h-auto">
                <rect x="10" y="3" width="40" height="44" rx="3" fill={C.panel} stroke={C.teal} strokeWidth="1.5" />
                <rect x="14" y="9" width="32" height="5" rx="1.5" fill={C.teal} opacity="0.3" />
                <rect x="14" y="9" width="16" height="5" rx="1.5" fill={C.teal} />
                <rect x="14" y="19" width="32" height="2.5" rx="1" fill="#fff" opacity="0.3" />
                <rect x="14" y="19" width="22" height="2.5" rx="1" fill={C.teal} opacity="0.7" />
                <rect x="14" y="25" width="32" height="2.5" rx="1" fill="#fff" opacity="0.3" />
                <rect x="14" y="25" width="18" height="2.5" rx="1" fill={C.teal} opacity="0.7" />
                <rect x="14" y="31" width="32" height="2.5" rx="1" fill="#fff" opacity="0.3" />
                <rect x="14" y="31" width="12" height="2.5" rx="1" fill={C.orange} />
                <circle cx="42" cy="40" r="4" fill={C.teal} />
                <path d="M40 40 l1.5 1.5 l3 -3" stroke="#000" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            onClick={gotoAccounts}
            data-testid="build-card-accounts"
          />
          <BuildCard
            num="04"
            name="Business Valuation"
            tag={locked ? "Coming soon" : "Triangulate the number"}
            status={locked ? "waitlist" : "live"}
            ctaLabel={locked ? "Join the shortlist" : (valuations.length ? "+ New valuation" : "Start valuation")}
            desc={locked
              ? "6-method valuation · industry-tuned output — notify me when it goes live."
              : "6-method methodology · 3-value output · 50-credit finalise"}
            icon={
              <svg viewBox="0 0 60 50" className="w-full h-auto">
                <rect x="6" y="6" width="48" height="38" rx="3" fill={C.panel} stroke={C.orange} strokeWidth="1.5" />
                <rect x="12" y="12" width="14" height="4" rx="1" fill={C.orange} opacity="0.5" />
                <rect x="12" y="20" width="36" height="2" rx="1" fill="#fff" opacity="0.25" />
                <rect x="12" y="25" width="32" height="2" rx="1" fill="#fff" opacity="0.25" />
                <rect x="14" y="33" width="9" height="6" rx="1" fill={C.teal} opacity="0.6" />
                <rect x="25" y="33" width="9" height="6" rx="1" fill={C.orange} />
                <rect x="36" y="33" width="9" height="6" rx="1" fill={C.teal} opacity="0.6" />
              </svg>
            }
            onClick={gotoValuation}
            data-testid="build-card-valuation"
          />
        </div>

        {/* Iter101/102: First-time founder walkthrough — text-only
            explainer card. Renders only when the founder has zero
            decks, and quietly disappears once they've built their first
            one. Video slot removed per iter102 — keep it simple. */}
        {!loading && decks.length === 0 && (
          <div
            data-testid="first-deck-walkthrough"
            className="rounded-2xl p-6 md:p-8 mb-8 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(10,139,133,0.14) 0%, rgba(245,158,11,0.06) 100%)",
              border: "1px solid rgba(123,211,206,0.22)",
            }}
          >
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-widest mb-3"
                style={{ backgroundColor: "rgba(245,158,11,0.15)", color: "#F59E0B" }}>
                <span>New here?</span>
              </div>
              <div className="font-extrabold tracking-tight text-white"
                style={{ fontSize: "clamp(22px, 3vw, 32px)", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                Build a funder-ready deck in under 10 minutes.
              </div>
              <div className="mt-3 text-white/60 leading-relaxed text-[14px]">
                Type your company name and Clinq sets up all the slides a funder expects — structured,
                branded and pre-labelled. You fill each one with AI help per slide, so nothing starts from a blank page.
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => navigate("/pitch/new")}
                  data-testid="walkthrough-cta"
                  className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 font-bold text-[13px] uppercase tracking-widest transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: "#F59E0B", color: "#1a1206", boxShadow: "0 6px 16px rgba(245,158,11,0.35)" }}
                >
                  Build your first deck →
                </button>
                <span className="text-white/40 text-[12px]">Takes ~10 minutes · 11 slides · PPTX + PDF export</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => navigate("/fm/new")}
                  data-testid="walkthrough-cta-fm"
                  className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 font-bold text-[13px] uppercase tracking-widest transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: "rgba(10,139,133,0.9)", color: "#fff" }}
                >
                  Or build your financial model →
                </button>
                <span className="text-white/40 text-[12px]">Takes ~15 minutes · 3+5-year forecast · Excel + PDF export</span>
              </div>
            </div>
          </div>
        )}

        {/* Your pitch decks */}
        <ArtifactList
          title="Your pitch decks"
          icon={<FileText size={16} />}
          loading={loading}
          items={decks}
          emptyText="No decks yet. Build one in ~10 minutes."
          emptyCtaLabel="Build your first deck"
          onEmptyCta={() => navigate("/pitch/new")}
          renderItem={(d) => {
            const isV2 = (d.design_version || "v1") === "v2";
            const lastExp = d.last_exported_at || d.exported_at;
            const staleMonths = lastExp
              ? Math.floor((Date.now() - new Date(lastExp).getTime()) / (30 * 86400000))
              : 0;
            return (
            <Link key={d.deck_id} to={`/pitch/${d.deck_id}`} data-testid={`deck-card-${d.deck_id}`}
              className="group block rounded-xl p-5 border transition-all hover:-translate-y-0.5 hover:shadow-lg relative"
              style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.12)" }}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-mono-num text-[10px] text-white/40 tracking-widest uppercase min-w-0 truncate">
                  {(d.industry || "—").replace("_", " ")} · {(d.funder_type || "—").replace("_", " ")}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={(e) => deleteDeck(d.deck_id, d.company_name, e)}
                    data-testid={`delete-deck-${d.deck_id}`}
                    className="text-white/30 hover:text-orange-400 transition-colors opacity-0 group-hover:opacity-100">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
              <div className="font-extrabold text-xl mt-1 tracking-tight" style={{ letterSpacing: "-0.02em" }}>{d.company_name}</div>
              <div className="text-sm text-white/55 mt-2 line-clamp-2">{d.one_liner || "Tap to continue editing."}</div>

              {/* Refresh nudge — exported deck older than 3 months */}
              {staleMonths >= 3 && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                  data-testid={`deck-refresh-nudge-${d.deck_id}`}
                  style={{ backgroundColor: "rgba(245,158,11,0.12)", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.4)" }}
                  title="Funders expect current numbers — update traction, projections and the ask, then re-export.">
                  <ClockCounterClockwise size={11} weight="bold" />
                  Exported {staleMonths}mo ago — refresh your numbers
                </div>
              )}

              {/* V2 design upgrade strip — only on legacy V1 decks */}
              {!isV2 && (
                <div className="mt-4 flex flex-wrap items-center gap-2"
                  data-testid={`deck-v2-upgrade-${d.deck_id}`}>
                  <button onClick={(e) => previewV2Design(d, e)} disabled={previewing === d.deck_id}
                    data-testid={`deck-preview-v2-${d.deck_id}`}
                    className="text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                    style={{ backgroundColor: "rgba(10, 139, 133, 0.18)", color: C.tealSoft, border: `1px solid rgba(10, 139, 133, 0.45)` }}
                    title="Render this deck with the new V2 design system. Downloads a .pptx so you can compare. Costs 2 credits.">
                    <PaintBrush size={11} weight="fill" />
                    {previewing === d.deck_id ? "Rendering…" : "Preview V2 · 2 cr"}
                    <ArrowSquareOut size={9} weight="bold" />
                  </button>
                  <button onClick={(e) => upgradeV2Design(d, e)} disabled={upgrading === d.deck_id}
                    data-testid={`deck-upgrade-v2-${d.deck_id}`}
                    className="text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-60 text-white/55 hover:text-white"
                    title="Permanently switch this deck to V2 design (free). All future exports will use the new templates.">
                    {upgrading === d.deck_id ? "Applying…" : "Apply V2"}
                  </button>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
                <span className="text-white/40 inline-flex items-center gap-1.5">
                  <Sparkle size={11} weight="fill" style={{ color: C.orange }} /> Pitch deck
                </span>
                <ArrowRight size={14} className="text-white/30 group-hover:translate-x-1 transition-transform" style={{ color: C.tealSoft }} />
              </div>
            </Link>
          );
          }}
        />

        {/* Your management accounts */}
        <div className="mt-12">
          <ArtifactList
            title="Your management accounts"
            icon={<Calculator size={16} />}
            loading={loading}
            items={accounts}
            emptyText="No monthly accounts yet. Run the 7-step reconciliation in ~15 minutes."
            emptyCtaLabel="Build this month"
            onEmptyCta={gotoAccounts}
            renderItem={(a) => (
              <Link key={a.account_id} to={`/mgmt/${a.account_id}`} data-testid={`account-card-${a.account_id}`}
                className="group block rounded-xl p-5 border transition-all hover:-translate-y-0.5 hover:shadow-lg relative"
                style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.12)" }}>
                <div className="flex items-start justify-between">
                  <div className="font-mono-num text-[10px] text-white/40 tracking-widest uppercase">
                    {a.target_month} · {(a.industry || "—").replace("_", " ")}
                  </div>
                  <button onClick={(e) => deleteAccount(a.account_id, a.company_name, e)}
                    data-testid={`delete-account-${a.account_id}`}
                    className="text-white/30 hover:text-orange-400 transition-colors opacity-0 group-hover:opacity-100">
                    <Trash size={14} />
                  </button>
                </div>
                <div className="font-extrabold text-xl mt-1 tracking-tight" style={{ letterSpacing: "-0.02em" }}>{a.company_name}</div>
                <div className="text-sm text-white/55 mt-2">
                  {a.transaction_count || 0} transactions parsed · {Object.values(a.gates || {}).filter(Boolean).length}/7 gate checks passed
                </div>

                {/* Sparkline teaser */}
                <AccountSparkline series={a.history_net_profit} delta={a.net_profit_delta_pct} monthsForCompany={a.months_for_company} />

                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
                  <span className="text-white/40 inline-flex items-center gap-1.5">
                    <Calculator size={11} weight="fill" style={{ color: C.tealSoft }} /> Management accounts
                  </span>
                  {a.months_for_company > 1 ? (
                    <span className="inline-flex items-center gap-1" style={{ color: C.tealSoft }} data-testid={`compare-cta-${a.account_id}`}>
                      Compare months <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  ) : (
                    <ArrowRight size={14} className="text-white/30 group-hover:translate-x-1 transition-transform" style={{ color: C.tealSoft }} />
                  )}
                </div>
              </Link>
            )}
          />
        </div>

        {/* Your financial models */}
        <div className="mt-12">
          <ArtifactList
            title="Your financial models"
            icon={<ChartLineUp size={16} />}
            loading={loading}
            items={models}
            emptyText="No financial models yet. Start with a 5-minute Business Profile."
            emptyCtaLabel="Build your first model"
            onEmptyCta={gotoModel}
            renderItem={(m) => (
              <Link key={m.fm_id} to={`/fm/${m.fm_id}`} data-testid={`model-card-${m.fm_id}`}
                className="group block rounded-xl p-5 border transition-all hover:-translate-y-0.5 hover:shadow-lg relative"
                style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.12)" }}>
                <div className="flex items-start justify-between">
                  <div className="font-mono-num text-[10px] text-white/40 tracking-widest uppercase">
                    {m.profile?.revenue_status === "pre_revenue" ? "Pre-revenue" : "Post-revenue"}
                  </div>
                  <button onClick={(e) => deleteModel(m.fm_id, m.name, e)}
                    data-testid={`delete-model-${m.fm_id}`}
                    className="text-white/30 hover:text-orange-400 transition-colors opacity-0 group-hover:opacity-100">
                    <Trash size={14} />
                  </button>
                </div>
                <div className="font-extrabold text-xl mt-1 tracking-tight" style={{ letterSpacing: "-0.02em" }}>{m.name}</div>
                <div className="text-sm text-white/55 mt-2 line-clamp-2">Tap to continue building.</div>
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
                  <span className="text-white/40 inline-flex items-center gap-1.5">
                    <ChartLineUp size={11} weight="fill" style={{ color: C.tealSoft }} /> {m.profile?.revenue_model || "—"} · F1–F5 monthly
                  </span>
                  <ArrowRight size={14} className="text-white/30 group-hover:translate-x-1 transition-transform" style={{ color: C.tealSoft }} />
                </div>
              </Link>
            )}
          />

          {/* Your valuations */}
          <ArtifactList
            title="Your business valuations"
            icon={<Calculator size={16} />}
            loading={loading}
            items={valuations}
            emptyText="No valuations yet. Triangulate a defensible number for your next raise."
            emptyCtaLabel="Start a valuation"
            onEmptyCta={gotoValuation}
            renderItem={(v) => (
              <Link key={v.valuation_id} to={`/valuation/${v.valuation_id}`} data-testid={`valuation-card-${v.valuation_id}`}
                className="group block rounded-xl p-5 border transition-all hover:-translate-y-0.5 hover:shadow-lg relative"
                style={{ backgroundColor: C.panel, borderColor: "rgba(245, 158, 11, 0.18)" }}>
                <div className="flex items-start justify-between">
                  <div className="font-mono-num text-[10px] text-white/40 tracking-widest uppercase">
                    {(v.sector || "—").replace("_", " ")} · {(v.stage || "—").replace("_", " ")}
                  </div>
                  {v.finalised_at && (
                    <span className="text-[9px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: "rgba(154, 230, 180, 0.12)", color: "#9AE6B4" }}>
                      Finalised
                    </span>
                  )}
                </div>
                <div className="font-extrabold text-xl mt-1 tracking-tight" style={{ letterSpacing: "-0.02em" }}>{v.company_name}</div>
                <div className="text-sm text-white/55 mt-2">
                  {(v.sub_stage || "growth").replace("_", " ")} stage
                  {v.linked_model_id && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: "rgba(123, 211, 206, 0.12)", color: C.tealSoft }}
                      data-testid={`valuation-linked-model-${v.valuation_id}`}
                      title="Inputs were imported from your financial model">
                      ◴ Linked to model
                    </span>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
                  <span className="text-white/40 inline-flex items-center gap-1.5">
                    <Calculator size={11} weight="fill" style={{ color: "#FCD34D" }} /> Methodology v1
                  </span>
                  <ArrowRight size={14} className="text-white/30 group-hover:translate-x-1 transition-transform" style={{ color: "#FCD34D" }} />
                </div>
              </Link>
            )}
          />
        </div>

        {/* My Exports — permanent deck library */}
        <div className="mt-12">
          <MyExports />
        </div>
      </main>
      <David autoOpenForSnapshot />
    </div>
  );
}

function BuildCard({ num, name, tag, status, ctaLabel, desc, icon, onClick, ...rest }) {
  const live = status === "live";
  return (
    <button onClick={onClick} {...rest}
      className="group text-left rounded-2xl p-6 border-2 transition-all hover:-translate-y-1 overflow-hidden relative"
      style={{
        backgroundColor: C.panel,
        borderColor: live ? "rgba(44, 197, 168, 0.4)" : "rgba(255,255,255,0.06)",
      }}>
      <div className="absolute -right-12 -top-12 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity blur-3xl"
        style={{ backgroundColor: C.teal }}></div>

      <div className="relative flex items-start justify-between mb-4">
        <div className="font-mono-num text-[10px] tracking-[0.2em]" style={{ color: live ? C.teal : "rgba(255,255,255,0.35)" }}>
          / {num}
        </div>
        {live ? (
          <span className="text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full inline-flex items-center gap-1"
            style={{ backgroundColor: C.teal, color: "#000" }}>
            <span className="w-1 h-1 rounded-full bg-current animate-pulse"></span>
            Live
          </span>
        ) : (
          <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/40">Soon</span>
        )}
      </div>

      <div className="relative w-20 h-16 mb-4">{icon}</div>

      <div className="relative font-extrabold text-2xl tracking-tight" style={{ letterSpacing: "-0.02em" }}>{name}</div>
      <div className="relative text-sm mt-1" style={{ color: live ? C.teal : "rgba(255,255,255,0.5)" }}>{tag}</div>
      <div className="relative text-xs text-white/45 mt-3 leading-relaxed">{desc}</div>

      <div className="relative mt-5 pt-5 border-t border-white/5 flex items-center justify-between text-sm font-semibold">
        <span style={{ color: live ? C.teal : "rgba(255,255,255,0.5)" }}>{ctaLabel}</span>
        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" style={{ color: live ? C.teal : "rgba(255,255,255,0.5)" }} />
      </div>
    </button>
  );
}

function ArtifactList({ title, icon, loading, items, emptyText, emptyCtaLabel, onEmptyCta, renderItem }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] inline-flex items-center gap-2" style={{ color: C.tealSoft }}>
          {icon} {title}
        </div>
        {!loading && items.length > 0 && (
          <span className="text-[10px] font-mono-num tracking-widest text-white/30">· {items.length}</span>
        )}
      </div>
      {loading ? (
        <div className="text-white/40 py-10 text-center text-sm" data-testid={`${title}-loading`}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl py-10 px-8 text-center border border-dashed"
          style={{ borderColor: "rgba(255,255,255,0.1)" }} data-testid={`${title}-empty`}>
          <p className="text-white/55 text-sm">{emptyText}</p>
          {onEmptyCta && (
            <button onClick={onEmptyCta}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-colors"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              <Plus size={14} weight="bold" /> {emptyCtaLabel}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(renderItem)}
        </div>
      )}
    </section>
  );
}



function AccountSparkline({ series, delta, monthsForCompany }) {
  const vals = (series || []).map((p) => Number(p.v) || 0);
  if (!vals.length || monthsForCompany < 2) {
    return (
      <div className="mt-3 rounded-md px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-bold"
        style={{ backgroundColor: "rgba(245, 158, 11, 0.08)", color: "#F59E0B" }} data-testid="sparkline-teaser-empty">
        Run next month to unlock month-on-month trends
      </div>
    );
  }
  const W = 240; const H = 36;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = (max - min) || 1;
  const stepX = vals.length > 1 ? W / (vals.length - 1) : 0;
  const path = vals.map((v, i) => `${i === 0 ? "M" : "L"} ${i * stepX} ${H - ((v - min) / range) * (H - 4) - 2}`).join(" ");
  const last = vals[vals.length - 1];
  const isUp = (delta ?? 0) >= 0;
  const colour = isUp ? "#0A8B85" : "#F87171";
  const deltaLabel = delta === null || delta === undefined ? "" : `${delta > 0 ? "+" : ""}${delta.toFixed(0)}% net profit MoM`;

  return (
    <div className="mt-3 rounded-md p-3" style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(123, 211, 206, 0.1)" }} data-testid="sparkline-teaser">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/45">Net profit · {vals.length} mo</div>
        {delta !== null && delta !== undefined && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold font-mono-num" style={{ color: colour }}>
            {isUp ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />} {deltaLabel}
          </span>
        )}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-1.5" preserveAspectRatio="none">
        <defs>
          <linearGradient id={`spark-grad-${last}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={colour} stopOpacity="0.35" />
            <stop offset="100%" stopColor={colour} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${path} L ${W} ${H} L 0 ${H} Z`} fill={`url(#spark-grad-${last})`} />
        <path d={path} fill="none" stroke={colour} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}
